<template>
    <div>
        <!--
            BLOCO C — item 40: períodos avaliativos da matriz curricular e a visão
            comparativa "previsto na matriz × efetivo no calendário da escola".

            Os dias letivos EFETIVOS não são calculados aqui. Eles vêm de
            `periodo-avaliativo-matriz/comparativo`, que por sua vez lê o calendário já
            com os eventos aceitos aplicados. A tela não repete regra de calendário.
        -->
        <el-alert type="info" :closable="false" class="u-mb-15"
            title="Os períodos avaliativos valem para todas as turmas desta matriz no período letivo escolhido."
            description="Os dias letivos previstos são a meta da matriz. O comparativo mostra quantos dias letivos o calendário da escola realmente entrega em cada período."></el-alert>

        <el-form label-position="top" @submit.native.prevent>
            <el-row :gutter="10" class="u-mb-15">
                <el-col :xs="24" :sm="8">
                    <el-form-item label="Período letivo" class="u-mb-0">
                        <el-select v-model="periodoId" style="width: 100%;" @change="carregar">
                            <el-option v-for="p in periodos" :key="p.id" :value="p.id" :label="p.nome"></el-option>
                        </el-select>
                    </el-form-item>
                </el-col>
                <el-col :xs="24" :sm="16" class="u-text-right u-mt-25">
                    <el-button v-if="podeEscrever" size="small" icon="el-icon-plus" @click="adicionar">ADICIONAR PERÍODO</el-button>
                    <el-button class="u-ml-10" v-if="podeEscrever" size="small" icon="el-icon-magic-stick" @click="sugerirBimestres">SUGERIR 4 BIMESTRES</el-button>
                    <el-button class="u-ml-10" v-if="podeEscrever" size="small" type="primary" icon="el-icon-check" @click="salvar">SALVAR</el-button>
                </el-col>
            </el-row>
        </el-form>

        <div class="container-table">
            <table class="table table-striped">
                <thead>
                    <tr>
                        <th style="width: 90px;">Ordem</th>
                        <th>Nome</th>
                        <th style="width: 170px;">Início</th>
                        <th style="width: 170px;">Fim</th>
                        <th style="width: 150px;" class="u-text-center">Dias letivos previstos</th>
                        <th style="width: 60px;"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(p, i) in itens" :key="i">
                        <td><el-input-number v-model="p.ordem" :min="1" :max="20" size="mini" controls-position="right" style="width: 100%;"></el-input-number></td>
                        <td><el-input v-model="p.nome" size="mini" placeholder="1º Bimestre"></el-input></td>
                        <td><el-date-picker v-model="p.data_inicial" type="date" size="mini" format="dd/MM/yyyy" value-format="yyyy-MM-dd" style="width: 100%;"></el-date-picker></td>
                        <td><el-date-picker v-model="p.data_final" type="date" size="mini" format="dd/MM/yyyy" value-format="yyyy-MM-dd" style="width: 100%;"></el-date-picker></td>
                        <td><el-input-number v-model="p.qtd_dias_letivos_previstos" :min="0" :max="300" size="mini" controls-position="right" style="width: 100%;"></el-input-number></td>
                        <td class="u-text-center">
                            <el-button v-if="podeEscrever" type="text" icon="el-icon-delete" @click="itens.splice(i, 1)"></el-button>
                        </td>
                    </tr>
                    <tr v-if="itens.length === 0"><td colspan="6" class="u-text-center">Nenhum período avaliativo definido.</td></tr>
                </tbody>
            </table>
        </div>

        <!-- ==================== COMPARATIVO — item 40 ==================== -->
        <h4 class="u-mt-25 u-mb-10">Comparativo: previsto na matriz × calendário</h4>

        <el-row :gutter="10" class="u-mb-10">
            <el-col :xs="24" :sm="10">
                <el-select v-model="instituicaoId" clearable filterable style="width: 100%;" size="small"
                    placeholder="Escola (em branco = calendário da rede)" @change="carregarComparativo">
                    <el-option v-for="i in instituicoes" :key="i.id" :value="i.id" :label="i.nome"></el-option>
                </el-select>
            </el-col>
            <el-col :xs="24" :sm="6">
                <el-button size="small" icon="el-icon-refresh" @click="carregarComparativo">ATUALIZAR</el-button>
            </el-col>
        </el-row>

        <el-alert v-if="comparativo && comparativo.erro" type="warning" :closable="false" :title="comparativo.erro"></el-alert>
        <el-alert v-else-if="comparativo && comparativo.aviso" type="info" :closable="false" :title="comparativo.aviso" class="u-mb-10"></el-alert>

        <div v-if="comparativo && !comparativo.erro" class="container-table">
            <table class="table table-striped">
                <thead>
                    <tr>
                        <th>Período</th>
                        <th class="u-text-center">De</th>
                        <th class="u-text-center">Até</th>
                        <th class="u-text-center">Previsto</th>
                        <th class="u-text-center">No calendário</th>
                        <th class="u-text-center">Saldo</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="l in comparativo.periodos" :key="l.id">
                        <td>{{ l.ordem }}º — {{ l.nome }}</td>
                        <td class="u-text-center">{{ dataBr(l.data_inicial) }}</td>
                        <td class="u-text-center">{{ dataBr(l.data_final) }}</td>
                        <td class="u-text-center">
                            <span v-if="l.dias_letivos_previstos !== null">{{ l.dias_letivos_previstos }}</span>
                            <el-tooltip v-else :content="l.aviso"><span>—</span></el-tooltip>
                        </td>
                        <td class="u-text-center">{{ l.dias_letivos_efetivos }}</td>
                        <td class="u-text-center">
                            <el-tag v-if="l.saldo === null" size="mini" type="info">sem previsão</el-tag>
                            <el-tag v-else-if="l.saldo < 0" size="mini" type="danger">{{ l.saldo }}</el-tag>
                            <el-tag v-else size="mini" type="success">+{{ l.saldo }}</el-tag>
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <th colspan="3">Total</th>
                        <th class="u-text-center">{{ comparativo.total_previsto }}</th>
                        <th class="u-text-center">{{ comparativo.total_efetivo }}</th>
                        <th class="u-text-center">{{ comparativo.total_efetivo - comparativo.total_previsto }}</th>
                    </tr>
                    <tr>
                        <td colspan="6">
                            <small>O calendário deste recorte tem {{ comparativo.total_dias_letivos_calendario }} dias letivos no ano.</small>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>
<script>
import { periodosLetivosMixin } from '@/shared/periodos-letivos'
export default {
    mixins: [periodosLetivosMixin],
    props: {
        matrizId: { type: Number, required: true },
        init: { type: Object, required: false, default: () => ({}) }
    },
    data: () => ({
        periodoId: null, itens: [],
        instituicaoId: null, instituicoes: [], comparativo: null
    }),
    computed: {
        /** Esconder o botão não é segurança: o backend recusa perfis fora da rede. */
        podeEscrever () { return [4, 5, 6, 7].includes(parseInt(this.$store.state.user.tipo)) }
    },
    mounted () {
        this.periodoId = this.$store.state.config.periodo ? this.$store.state.config.periodo.id : null

        this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/instituicao/getAll`,
            this.$store.getters.getHeaderAuth
        ).then(r => { this.instituicoes = Array.isArray(r.data) ? r.data : [] }).catch(() => {})

        this.carregar()
    },
    methods: {
        carregar () {
            this.itens = []
            this.comparativo = null
            if (!this.periodoId) return

            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/periodo-avaliativo-matriz/getByMatriz/${this.matrizId}/${this.periodoId}`,
                this.$store.getters.getHeaderAuth
            ).then(r => {
                this.itens = (Array.isArray(r.data) ? r.data : []).map(p => ({
                    ordem: p.ordem, nome: p.nome,
                    data_inicial: (p.data_inicial || '').substring(0, 10),
                    data_final: (p.data_final || '').substring(0, 10),
                    qtd_dias_letivos_previstos: p.qtd_dias_letivos_previstos
                }))
                this.carregarComparativo()
            }).catch(e => this.$store.commit('throwException', e))
        },
        adicionar () {
            this.itens.push({ ordem: this.itens.length + 1, nome: '', data_inicial: '', data_final: '',
                qtd_dias_letivos_previstos: null })
        },
        /**
         * Atalho de digitação: 4 bimestres com os nomes usuais e SEM datas.
         * As datas continuam sendo do operador — não se inventa divisão do ano letivo.
         */
        sugerirBimestres () {
            this.itens = [1, 2, 3, 4].map(n => ({
                ordem: n, nome: n + 'º Bimestre', data_inicial: '', data_final: '',
                qtd_dias_letivos_previstos: null
            }))
        },
        salvar () {
            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/periodo-avaliativo-matriz/salvarLote`,
                { matriz_curricular_id: this.matrizId, periodo_letivo_id: this.periodoId, itens: this.itens },
                this.$store.getters.getHeaderAuth
            ).then(r => {
                if (r.data && r.data.erro) { window.eventBus.$emit('alert', { msg: r.data.erro, type: 'warning' }); return }
                window.eventBus.$emit('alert', { msg: r.data.mensagem || 'Períodos avaliativos salvos.', type: 'success' })
                this.carregar()
            }).catch(e => this.$store.commit('throwException', e))
        },
        carregarComparativo () {
            this.comparativo = null
            if (!this.periodoId) return

            const base = `${this.$store.state.config.baseUrlApi}/api/periodo-avaliativo-matriz/comparativo/${this.matrizId}/${this.periodoId}`
            const url = this.instituicaoId ? `${base}/${this.instituicaoId}` : base

            this.$axios.get(url, this.$store.getters.getHeaderAuth)
                .then(r => { this.comparativo = r.data })
                .catch(e => this.$store.commit('throwException', e))
        },
        dataBr (d) {
            if (!d) return '—'
            const p = d.substring(0, 10).split('-')
            return `${p[2]}/${p[1]}/${p[0]}`
        }
    }
}
</script>
